import type { DocumentItem, NormalizeWarning } from './model.js';
import type { DocumentMapper } from './mapper.js';
import { readField, resolveMapper } from './mapper.js';

/**
 * The indexed form the rest of the library reads. Built once per data change
 * and treated as immutable.
 */
export interface DocumentIndex {
  byId: Map<string, DocumentItem>;
  /** Parent id -> ordered child ids. `null` holds the roots. */
  childrenOf: Map<string | null, string[]>;
  roots: string[];
}

export interface NormalizeResult {
  index: DocumentIndex;
  items: DocumentItem[];
  warnings: NormalizeWarning[];
}

export interface NormalizeOptions<T> {
  mapper?: DocumentMapper<T>;
}

/** Depth-first flatten of nested (`children`) input into flat + parentId form. */
function flatten<T>(
  data: readonly T[],
  map: (raw: T, index: number) => DocumentItem,
  childrenField: DocumentMapper<T>['children'],
  parentId: string | null,
  out: DocumentItem[],
  counter: { n: number },
): void {
  for (const raw of data) {
    const item = map(raw, counter.n++);
    // An explicit parentId on the row wins; otherwise inherit from nesting.
    if (item.parentId == null && parentId !== null) item.parentId = parentId;
    out.push(item);

    const kids = childrenField !== undefined ? readField(raw, childrenField) : (raw as Record<string, unknown>)?.['children'];
    if (Array.isArray(kids) && kids.length > 0) {
      flatten(kids as T[], map, childrenField, item.id, out, counter);
    }
  }
}

/**
 * Turn arbitrary consumer data into a `DocumentIndex`.
 *
 * Accepts flat rows, nested `children` trees, or a mix. Every structural
 * problem is collected as a warning and repaired — this never throws, because
 * a library that dies on one bad row is unusable in production.
 */
export function normalize<T>(data: readonly T[] | null | undefined, options: NormalizeOptions<T> = {}): NormalizeResult {
  const warnings: NormalizeWarning[] = [];
  const map = resolveMapper(options.mapper);

  const flatItems: DocumentItem[] = [];
  if (Array.isArray(data) && data.length > 0) {
    flatten(data, map, options.mapper?.children, null, flatItems, { n: 0 });
  }

  // --- de-duplicate ids ------------------------------------------------
  const byId = new Map<string, DocumentItem>();
  const items: DocumentItem[] = [];
  for (const item of flatItems) {
    if (item.id.startsWith('__de_missing_')) {
      warnings.push({ code: 'missing-id', id: item.id, message: `Item "${item.name}" has no id; a synthetic one was assigned.` });
    }
    if (byId.has(item.id)) {
      warnings.push({ code: 'duplicate-id', id: item.id, message: `Duplicate id "${item.id}"; the later item was dropped.` });
      continue;
    }
    byId.set(item.id, item);
    items.push(item);
  }

  // --- repair parent links ---------------------------------------------
  for (const item of items) {
    if (item.parentId == null) continue;

    if (item.parentId === item.id) {
      warnings.push({ code: 'self-parent', id: item.id, message: `Item "${item.id}" is its own parent; treated as a root.` });
      item.parentId = null;
      continue;
    }
    const parent = byId.get(item.parentId);
    if (!parent) {
      warnings.push({
        code: 'missing-parent',
        id: item.id,
        message: `Parent "${item.parentId}" of "${item.id}" was not found; treated as a root.`,
      });
      item.parentId = null;
      continue;
    }
    if (parent.type !== 'folder') {
      warnings.push({
        code: 'file-as-parent',
        id: item.id,
        message: `Parent "${parent.id}" of "${item.id}" is a file, not a folder; it was promoted to a folder.`,
      });
      parent.type = 'folder';
    }
  }

  // --- break cycles ------------------------------------------------------
  // Walk each node to a root; if we revisit a node in the same walk, the chain
  // loops, so cut the link at the offender.
  const settled = new Set<string>();
  for (const item of items) {
    if (settled.has(item.id)) continue;
    const seen = new Set<string>();
    let cursor: DocumentItem | undefined = item;
    while (cursor && cursor.parentId != null) {
      if (seen.has(cursor.id)) {
        warnings.push({ code: 'cycle', id: cursor.id, message: `Cycle detected at "${cursor.id}"; the parent link was cut.` });
        cursor.parentId = null;
        break;
      }
      seen.add(cursor.id);
      cursor = byId.get(cursor.parentId);
    }
    for (const id of seen) settled.add(id);
  }

  // --- build the child index --------------------------------------------
  const childrenOf = new Map<string | null, string[]>();
  const roots: string[] = [];
  for (const item of items) {
    const key = item.parentId ?? null;
    const bucket = childrenOf.get(key);
    if (bucket) bucket.push(item.id);
    else childrenOf.set(key, [item.id]);
    if (key === null) roots.push(item.id);
  }
  if (!childrenOf.has(null)) childrenOf.set(null, []);

  return { index: { byId, childrenOf, roots }, items, warnings };
}

/** An empty index, useful as an initial value. */
export function emptyIndex(): DocumentIndex {
  return { byId: new Map(), childrenOf: new Map([[null, []]]), roots: [] };
}
